import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import FormFiller from './FormFiller';
import { useAuth } from './AuthContext';
import { API_BASE_URL } from './config';
import { PRIMARY_BTN, SECONDARY_BTN, CARD } from './ui';

// The whole application, as a one-time account sees it: the survey it was
// created for, and nothing else. There is no navigation because there is
// nowhere else to go — the API returns exactly one form to these accounts, and
// the account itself stops existing the moment that form is submitted.
const OneTimeSurvey = () => {
  const { user, logout } = useAuth();
  const [form, setForm] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | ready | missing | done

  useEffect(() => {
    let cancelled = false;
    const fetchAssignedForm = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/forms`);
        if (cancelled) return;
        const assigned = response.data[0];
        setForm(assigned || null);
        setStatus(assigned ? 'ready' : 'missing');
      } catch (error) {
        console.error('Failed to load the assigned survey:', error);
        if (!cancelled) setStatus('missing');
      }
    };
    fetchAssignedForm();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSubmitted = useCallback(() => setStatus('done'), []);

  return (
    <div className="min-h-screen bg-slate-900 text-white">
      <header className="bg-gray-900/95 backdrop-blur-sm border-b border-purple-500/20 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
          <span className="text-sm text-gray-400 truncate">{user?.email}</span>
          <button
            onClick={logout}
            className="bg-red-500/10 hover:bg-red-500/20 text-red-400 hover:text-red-300 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 border border-red-500/20 hover:border-red-500/40"
          >
            Exit
          </button>
        </div>
      </header>

      <main className="min-h-screen">
        {status === 'loading' && (
          <div className="max-w-2xl mx-auto px-4 py-16 text-center text-gray-400">
            Loading your survey...
          </div>
        )}

        {status === 'missing' && (
          <div className="max-w-2xl mx-auto px-4 py-16">
            <div className={`${CARD} p-8 text-center`}>
              <h1 className="text-2xl font-bold text-white mb-2">No survey attached</h1>
              <p className="text-gray-400 mb-6">
                This login isn't pointing at a survey any more. Whoever sent you the password can
                issue a new one.
              </p>
              <button onClick={logout} className={SECONDARY_BTN}>Close</button>
            </div>
          </div>
        )}

        {status === 'ready' && form && (
          <FormFiller formId={form.id} onSubmitted={handleSubmitted} />
        )}

        {status === 'done' && (
          <div className="max-w-2xl mx-auto px-4 py-16">
            <div className={`${CARD} p-8 text-center`}>
              <h1 className="text-2xl font-bold text-white mb-2">Thanks — that's everything</h1>
              <p className="text-gray-400 mb-6">
                Your answers have been sent. This login was good for the one survey and has now
                been closed.
              </p>
              <button onClick={logout} className={PRIMARY_BTN}>Done</button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default OneTimeSurvey;
